import React, { useState, useMemo } from "react";
import {
  Box, Button, TextField, Stack, Pagination, ToggleButton, ToggleButtonGroup, Tooltip, MenuItem,
} from "@mui/material";
import { Plus, AlertTriangle } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ticketService } from "../../api/services/ticketService";
import { slaService } from "../../api/services/slaService";
import { identityService } from "../../api/services/identityService";
import { projectService } from "../../api/services/projectService";
import { useAuth } from "../../contexts/AuthContext";
import DataTable from "../../components/DataTable";
import { PageHeader, StatusChip } from "../../components/Common";
import AssigneeAvatarFilter from "../../components/AssigneeAvatarFilter";
import TicketCreateDialog from "./TicketCreateDialog";

const PAGE_SIZE = 20;

export default function TicketListView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useAuth();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState("all"); // "all" | "mine" -- server-side assignee filter
  const [statusId, setStatusId] = useState("");
  const [priorityId, setPriorityId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [pickedAssigneeId, setPickedAssigneeId] = useState(null); // a specific person, picked via avatar
  const [createOpen, setCreateOpen] = useState(false);

  const { data: project } = useQuery({ queryKey: ["project", projectId], queryFn: () => projectService.get(projectId) });
  const { data: statuses } = useQuery({ queryKey: ["statuses"], queryFn: slaService.listStatuses, staleTime: Infinity });
  const { data: priorities } = useQuery({ queryKey: ["priorities"], queryFn: slaService.listPriorities, staleTime: Infinity });
  const { data: teams } = useQuery({ queryKey: ["teams"], queryFn: identityService.listTeams });
  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: identityService.listEmployees });

  const statusById = useMemo(() => Object.fromEntries((statuses || []).map((s) => [s.id, s])), [statuses]);
  const priorityById = useMemo(() => Object.fromEntries((priorities || []).map((p) => [p.id, p])), [priorities]);
  const employeeById = useMemo(() => Object.fromEntries((employees || []).map((e) => [e.id, e])), [employees]);

  // Avatar strip shows the selected team's members once a team is picked
  // (matching the same narrowing used in ticket creation's assignee field),
  // otherwise everyone in the company.
  const selectedTeam = teams?.find((t) => t.id === teamId);
  const avatarCandidates = selectedTeam
    ? (employees || []).filter((e) => selectedTeam.member_employee_ids.includes(e.id))
    : (employees || []);

  // "Mine" with no resolvable employee_id (e.g. an admin account whose ATS
  // role is attached via platform_user_id, not a real Employee row) must
  // NOT silently fall back to "no filter" -- that would send the exact same
  // request as "All Tickets" and show identical results. Gate the query
  // instead, same as the Board page already does.
  const canFilterMine = Boolean(me?.employee_id);
  const listParams = {
    project_id: projectId,
    search: search || undefined,
    page,
    page_size: PAGE_SIZE,
    status_id: statusId || undefined,
    priority_id: priorityId || undefined,
    team_id: teamId || undefined,
    assignee_employee_id: pickedAssigneeId || (scope === "mine" ? me?.employee_id : undefined),
  };

  const { data, isLoading } = useQuery({
    queryKey: ["tickets", listParams],
    queryFn: () => ticketService.list(listParams),
    keepPreviousData: true,
    enabled: Boolean(projectId) && (scope !== "mine" || pickedAssigneeId || canFilterMine),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const items = data?.items || [];

  const resetToPageOne = (setter) => (value) => { setter(value); setPage(1); };

  const handleScopeChange = (value) => {
    setScope(value);
    setPickedAssigneeId(null);
    setPage(1);
  };

  const handleAvatarPick = (employeeId) => {
    setPickedAssigneeId(employeeId);
    setScope("all"); // an explicit person takes over from the All/Mine toggle
    setPage(1);
  };

  const columns = [
    { key: "ticket_number", label: "Ticket #", render: (r) => <strong>{r.ticket_number}</strong> },
    { key: "subject", label: "Subject" },
    {
      key: "status_id", label: "Status",
      render: (r) => {
        const s = statusById[r.status_id];
        return <StatusChip value={s?.state_group} label={s?.name || `#${r.status_id}`} />;
      },
    },
    {
      key: "priority_id", label: "Priority",
      render: (r) => {
        const p = priorityById[r.priority_id];
        if (!p) return "-";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
            {p.name}
          </Box>
        );
      },
    },
    {
      key: "team_id", label: "Team",
      render: (r) => (teams || []).find((t) => t.id === r.team_id)?.name || "-",
    },
    {
      key: "assignee_employee_id", label: "Assignee",
      render: (r) => employeeById[r.assignee_employee_id]?.name || "Unassigned",
    },
    {
      key: "sla", label: "SLA",
      render: (r) => {
        const breached = r.sla_response_breached || r.sla_resolution_breached;
        if (!breached) return null;
        return (
          <Tooltip title={r.sla_resolution_breached ? "Resolution SLA breached" : "Response SLA breached"}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#B00020", fontWeight: 700 }}>
              <AlertTriangle size={15} /> Breached
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: "created_at", label: "Created",
      render: (r) => new Date(r.created_at).toLocaleString(),
    },
  ];

  return (
    <Box>
      <PageHeader
        title={project ? `${project.name} · List` : "Tickets"}
        subtitle={data ? `${data.total} total` : ""}
        action={
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
            New Ticket
          </Button>
        }
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small" placeholder="Search subject or ticket number..." sx={{ width: 280 }}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <ToggleButtonGroup
          size="small" exclusive value={scope}
          onChange={(_, v) => v && handleScopeChange(v)}
        >
          <ToggleButton value="all">All Tickets</ToggleButton>
          <ToggleButton value="mine">My Tickets</ToggleButton>
        </ToggleButtonGroup>
        <AssigneeAvatarFilter employees={avatarCandidates} selectedId={pickedAssigneeId} onChange={handleAvatarPick} />
        <TextField
          select size="small" label="Status" value={statusId} sx={{ width: 160 }}
          onChange={(e) => resetToPageOne(setStatusId)(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {(statuses || []).map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Priority" value={priorityId} sx={{ width: 150 }}
          onChange={(e) => resetToPageOne(setPriorityId)(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {(priorities || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Team" value={teamId} sx={{ width: 170 }}
          onChange={(e) => { resetToPageOne(setTeamId)(e.target.value); setPickedAssigneeId(null); }}
        >
          <MenuItem value="">All</MenuItem>
          {(teams || []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
      </Stack>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        emptyMessage={
          scope === "mine" && !canFilterMine
            ? "Your account isn't linked to an employee record, so \"My Tickets\" can't be filtered"
            : scope === "mine" ? "No tickets assigned to you" : "No tickets match these filters"
        }
        onRowClick={(row) => navigate(`/tickets/${row.id}`)}
        getRowSx={(row) =>
          (row.sla_response_breached || row.sla_resolution_breached)
            ? { backgroundColor: "rgba(176, 0, 32, 0.08)" }
            : {}
        }
        footer={
          totalPages > 1 && (
            <Stack direction="row" justifyContent="center">
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" />
            </Stack>
          )
        }
      />

      <TicketCreateDialog
        open={createOpen}
        projectId={projectId}
        onClose={() => {
          setCreateOpen(false);
          queryClient.invalidateQueries({ queryKey: ["tickets"] });
        }}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ["tickets"] })}
      />
    </Box>
  );
}
